import { orders as mockOrders } from '@/mocks/orders';
import type { Order, OrderStatus } from '@/domain/types';
import { requireSupabase } from '@/lib/supabase';

export const orders: Order[] = mockOrders;

export function getOrdersByUserId(userId: string) {
  return orders.filter((order) => order.userId === userId);
}

export async function getOrderById(id: string): Promise<Order | null> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('orders')
    .select(ORDER_SELECT)
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data ? mapOrder(data as OrderRow) : null;
}

type OrderRow = {
  id: string;
  order_ref: string;
  user_id: string;
  product_id: string;
  plan_id: string;
  amount: number;
  currency: string;
  status: OrderStatus;
  created_at: string;
  paid_at: string | null;
  products?: { name?: string | null } | null;
  plans?: { name?: string | null } | null;
  profiles?: { email?: string | null; name?: string | null } | null;
};

function mapOrder(row: OrderRow): Order {
  return {
    id: row.id,
    orderRef: row.order_ref,
    userId: row.profiles?.email || row.user_id,
    productId: row.product_id,
    planId: row.plan_id,
    productName: row.products?.name || row.product_id,
    planName: row.plans?.name || row.plan_id,
    amount: Number(row.amount),
    currency: row.currency,
    status: row.status,
    createdAt: row.created_at,
    paidAt: row.paid_at,
  };
}

const ORDER_SELECT = `
  *,
  profiles:user_id(email,name),
  products:product_id(name),
  plans:plan_id(name)
`;

export async function listAdminOrders() {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('orders')
    .select(ORDER_SELECT)
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return (data as OrderRow[]).map(mapOrder);
}

export async function payOrder(orderId: string): Promise<void> {
  const supabase = requireSupabase();
  const { error } = await supabase.rpc('customer_pay_order', { p_order_id: orderId });
  if (error) throw error;
}

export async function listCurrentUserOrders(): Promise<Order[]> {
  const supabase = requireSupabase();
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError) {
    throw userError;
  }

  if (!user) {
    return [];
  }

  const { data, error } = await supabase
    .from('orders')
    .select(ORDER_SELECT)
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return ((data as OrderRow[]) ?? []).map((row) => ({
    ...mapOrder(row),
    userId: user.id,
  }));
}

export async function updateAdminOrderStatus(id: string, status: OrderStatus) {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('orders')
    .update({
      status,
      paid_at: status === 'paid' ? new Date().toISOString() : null,
    })
    .eq('id', id)
    .select(ORDER_SELECT)
    .single();

  if (error) throw error;

  // Also activate the license when order is marked as paid
  if (status === 'paid') {
    await supabase
      .from('licenses')
      .update({ status: 'active' })
      .eq('order_id', id);
  }

  return mapOrder(data as OrderRow);
}

export async function deleteAdminOrder(id: string) {
  const supabase = requireSupabase();
  const { error } = await supabase.from('orders').delete().eq('id', id);

  if (error) {
    throw error;
  }
}
